import { ERROR_CODES, MagicLinkRequestSchema } from "@l30/shared";
import { Button, Input } from "@l30/ui";
import { createRoute } from "@tanstack/react-router";
import { useEffect, useRef, useState, type FormEvent } from "react";

import { ApiError, useRequestMagicLink } from "../../api/portal.js";
import { PartnerWordmark } from "../../components/brand/partner-wordmark.js";
import { currentBrand } from "../../lib/partner-brand.js";
import { tenantScopeRoute } from "../tenant-scope.js";

/**
 * Portal sign-in surface — magic-link request form.
 *
 * Public route (no shell, no `/me` gate). Renders the form,
 * fires `POST /api/auth/magic-link/request`, then shows a
 * neutral "check your email" confirmation regardless of whether
 * the email matches a portal user (email-enumeration defense
 * mirrors the API contract: server always returns 202).
 *
 * Cloudflare Turnstile loads when `VITE_CF_TURNSTILE_SITE_KEY` is
 * set (mirrors staff booking). Turnstile failures surface as a
 * retryable client error — they must NOT flip to the "check your
 * email" confirmation (that would teach bots the form accepts
 * unverified posts).
 *
 * Tenant comes from `/t/$tenantSlug` (and `X-Portal-Tenant` on
 * family hosts); the form doesn't ask for a tenant slug.
 */
export const portalSignInRoute = createRoute({
  getParentRoute: () => tenantScopeRoute,
  path: "/portal/sign-in",
  component: PortalSignIn,
});

const TURNSTILE_SITE_KEY = import.meta.env.VITE_CF_TURNSTILE_SITE_KEY as string | undefined;

function PortalSignIn() {
  const brand = currentBrand();
  const [email, setEmail] = useState("");
  const [clientError, setClientError] = useState<string | null>(null);
  const [submitted, setSubmitted] = useState(false);
  const [turnstileToken, setTurnstileToken] = useState<string | null>(null);
  const [turnstileEpoch, setTurnstileEpoch] = useState(0);
  const turnstileRef = useRef<HTMLDivElement>(null);
  const turnstileWidgetId = useRef<string | null>(null);
  const requestLink = useRequestMagicLink();

  function resetTurnstile() {
    setTurnstileToken(null);
    // Prefer in-place reset only while the widget node is still mounted.
    // After the success screen, turnstileRef is null but widgetId may still
    // point at a detached widget — reset() can "succeed" without rendering
    // a fresh token on the next form mount, so force remount in that case.
    if (turnstileRef.current && turnstileWidgetId.current != null) {
      const w = window as unknown as {
        turnstile?: { reset: (widgetId?: string) => void };
      };
      if (w.turnstile) {
        try {
          w.turnstile.reset(turnstileWidgetId.current);
          return;
        } catch {
          // fall through to remount
        }
      }
    }
    turnstileWidgetId.current = null;
    setTurnstileEpoch((n) => n + 1);
  }

  // Load Cloudflare Turnstile script + render widget. Only when the
  // site key is configured at build time; otherwise the widget is
  // omitted and the server-side verifier short-circuits to ok
  // (non-prod) / fail-closed (prod).
  useEffect(() => {
    if (!TURNSTILE_SITE_KEY) return;
    const SCRIPT_ID = "cf-turnstile-script";
    if (!document.getElementById(SCRIPT_ID)) {
      const s = document.createElement("script");
      s.id = SCRIPT_ID;
      s.src = "https://challenges.cloudflare.com/turnstile/v0/api.js";
      s.async = true;
      s.defer = true;
      document.head.appendChild(s);
    }
    let cancelled = false;
    turnstileWidgetId.current = null;
    function attempt() {
      const w = window as unknown as {
        turnstile?: {
          render: (
            el: HTMLElement,
            opts: { sitekey: string; callback: (token: string) => void },
          ) => string;
        };
      };
      if (cancelled) return;
      if (!w.turnstile || !turnstileRef.current) {
        setTimeout(attempt, 200);
        return;
      }
      turnstileWidgetId.current = w.turnstile.render(turnstileRef.current, {
        sitekey: TURNSTILE_SITE_KEY!,
        callback: (token: string) => setTurnstileToken(token),
      });
    }
    attempt();
    return () => {
      cancelled = true;
    };
  }, [turnstileEpoch]);

  const handleSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setClientError(null);

    const parsed = MagicLinkRequestSchema.safeParse({
      email,
      ...(turnstileToken ? { turnstileToken } : {}),
    });
    if (!parsed.success) {
      setClientError(parsed.error.issues[0]?.message ?? "Enter a valid email");
      return;
    }

    // Token is single-use once Cloudflare verifies it server-side —
    // clear immediately so a retry cannot reuse a spent token.
    const submittedToken = turnstileToken;
    setTurnstileToken(null);

    requestLink.mutate(
      {
        ...parsed.data,
        ...(submittedToken ? { turnstileToken: submittedToken } : {}),
      },
      {
        onSuccess: () => setSubmitted(true),
        onError: (err) => {
          // Turnstile / anti-abuse 403: show a retry message and mint a
          // fresh widget token. Do NOT flip to the enumeration-safe
          // confirmation — that path is only for constant-202 outcomes.
          if (err instanceof ApiError && err.code === ERROR_CODES.MAGIC_LINK_TURNSTILE_FAILED) {
            setClientError(err.message);
            resetTurnstile();
            return;
          }
          // Network / 5xx: still flip to "submitted" so the UX stays
          // uniform with the email-enumeration contract. Operators
          // see the audit log; the caller cannot distinguish.
          setSubmitted(true);
        },
      },
    );
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-surface-muted px-4">
      <div className="w-full max-w-md space-y-6 rounded-lg border bg-surface p-8 shadow-sm">
        <header className="flex flex-col items-center gap-3 text-center">
          <PartnerWordmark brand={brand} size="lg" />
          <h1 className="text-2xl font-semibold text-fg">Sign in to your portal</h1>
        </header>

        {submitted ? (
          <div className="space-y-3 rounded-md bg-success-bg p-4 text-sm text-success-fg">
            <p className="font-medium">Check your email.</p>
            <p>
              If <span className="font-medium">{email}</span> is registered, we&rsquo;ve sent a
              one-time sign-in link. The link is valid for 24 hours.
            </p>
            <button
              type="button"
              className="text-xs underline hover:no-underline"
              onClick={() => {
                setSubmitted(false);
                setEmail("");
                setClientError(null);
                resetTurnstile();
              }}
            >
              Use a different email
            </button>
          </div>
        ) : (
          <form className="space-y-4" onSubmit={handleSubmit} noValidate>
            <p className="rounded-md bg-surface-muted px-3 py-2 text-xs leading-relaxed text-fg-subtle">
              Confirm you are on your organisation&rsquo;s portal host (
              <span className="font-medium text-fg">*.portal.latitude30.co</span> or the host your
              organisation gave you) before entering your email.
            </p>
            <Input
              id="portal-email"
              label="Email"
              type="email"
              autoComplete="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              error={clientError ?? undefined}
              disabled={requestLink.isPending}
              placeholder="you@example.com"
            />
            {TURNSTILE_SITE_KEY && (
              <div className="pt-1">
                <div key={turnstileEpoch} ref={turnstileRef} />
              </div>
            )}
            <Button type="submit" className="w-full" disabled={requestLink.isPending}>
              {requestLink.isPending ? "Sending link…" : "Send sign-in link"}
            </Button>
            <p className="text-center text-xs text-fg-subtle">
              We&rsquo;ll email you a one-time link. No password needed.
            </p>
          </form>
        )}
      </div>
    </div>
  );
}
